/**
 * Image processing utilities for avatar uploads
 * Handles resizing, compression, and validation to ensure compatibility
 * with backend ImageMagick processing.
 */

const MAX_DIMENSION = 1024;
const MAX_FILE_SIZE = 500 * 1024; // 500KB
const JPEG_QUALITY = 0.85;
const MIN_QUALITY = 0.5;

export const validateImage = (file) => {
  return new Promise((resolve) => {
    // Check basic MIME type
    if (!file.type.startsWith('image/')) {
      resolve({ valid: false, error: 'File is not an image' });
      return;
    }

    // Try to actually decode the image
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      // Check for animated GIF (we can't detect this fully client-side, but we can warn)
      if (file.type === 'image/gif' && file.size > 100 * 1024) {
        // Large GIFs are likely animated
        resolve({
          valid: true,
          warning: 'Animated GIFs may not process correctly. Consider using a static image.',
          image: img,
        });
        return;
      }

      resolve({ valid: true, image: img });
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ valid: false, error: 'Image file is corrupted or in an unsupported format' });
    };

    img.src = objectUrl;
  });
};

const calculateResizedDimensions = (img, maxDimension) => {
  let { width, height } = img;

  if (width <= maxDimension && height <= maxDimension) {
    return { width, height };
  }

  if (width > height) {
    height = Math.round((height / width) * maxDimension);
    width = maxDimension;
  } else {
    width = Math.round((width / height) * maxDimension);
    height = maxDimension;
  }

  return { width, height };
};

const compressImage = async (img, maxDimension, maxFileSize) => {
  const { width, height } = calculateResizedDimensions(img, maxDimension);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');

  // Fill with white background (for transparency)
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);

  // Draw the image
  ctx.drawImage(img, 0, 0, width, height);

  // Try to compress to target size
  let quality = JPEG_QUALITY;
  let blob;

  while (quality >= MIN_QUALITY) {
    blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', quality);
    });

    if (blob.size <= maxFileSize) {
      break;
    }

    quality -= 0.1;
  }

  // If still too large, reduce dimensions further
  if (blob.size > maxFileSize && maxDimension > 512) {
    return compressImage(img, Math.floor(maxDimension * 0.75), maxFileSize);
  }

  return { blob, dimensions: { width, height }, quality };
};

export const processAvatarImage = async (file) => {
  // Step 1: Validate the image
  const validation = await validateImage(file);

  if (!validation.valid) {
    return { success: false, error: validation.error };
  }

  const img = validation.image;
  const originalSize = file.size;

  // Step 2: Check if processing is needed
  const needsResize = img.width > MAX_DIMENSION || img.height > MAX_DIMENSION;
  const needsCompression = file.size > MAX_FILE_SIZE;

  // If image is already small enough and is JPEG, just use it as-is
  if (!needsResize && !needsCompression && file.type === 'image/jpeg') {
    const previewUrl = URL.createObjectURL(file);
    return {
      success: true,
      file,
      previewUrl,
      originalSize,
      newSize: file.size,
      dimensions: { width: img.width, height: img.height },
      wasProcessed: false,
      warning: validation.warning,
    };
  }

  // Step 3: Process the image
  try {
    const { blob, dimensions, quality } = await compressImage(img, MAX_DIMENSION, MAX_FILE_SIZE);

    // Create a new File object
    const processedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });

    const previewUrl = URL.createObjectURL(blob);

    return {
      success: true,
      file: processedFile,
      previewUrl,
      originalSize,
      newSize: blob.size,
      dimensions,
      quality: Math.round(quality * 100),
      wasProcessed: true,
      warning: validation.warning,
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to process image: ${error.message}`,
    };
  }
};

export const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
